import { useMemo, useState } from "react";
import { Target } from "lucide-react";
import { ToolLayout, TwoCol } from "@/components/tools/ToolLayout";
import { SectionCard, Field, MoneyInput, SliderRow, StatCard, InfoRow, compactINR } from "@/components/tools/kit";
import { ToolAdvisor } from "@/components/tools/ToolAdvisor";
import { goalPlanner } from "@/lib/calculators";

export default function GoalPlanner() {
  const [goalToday, setGoal] = useState(5000000);
  const [years, setYears] = useState(10);
  const [rate, setRate] = useState(12);
  const [inflation, setInflation] = useState(6);
  const [currentSavings, setSavings] = useState(500000);
  const r = useMemo(() => goalPlanner({ goalToday, years, rate, inflation, currentSavings }), [goalToday, years, rate, inflation, currentSavings]);
  return (
    <ToolLayout title="Goal Planner" subtitle="Find the exact SIP or lumpsum to reach your goal" icon={Target} accent="#D96B43">
      <TwoCol
        left={
          <SectionCard title="Your goal">
            <div className="space-y-5">
              <Field label="Goal amount (today's value)"><MoneyInput testid="goal-amount" value={goalToday} onChange={setGoal} helper={compactINR(goalToday)} /></Field>
              <Field label="Years to goal" hint={years + " yrs"}><SliderRow testid="goal-years" value={years} min={1} max={40} suffix=" yr" onChange={setYears} /></Field>
              <Field label="Expected return (p.a.)" hint={rate + "%"}><SliderRow testid="goal-rate" value={rate} min={1} max={20} step={0.5} suffix="%" onChange={setRate} /></Field>
              <Field label="Goal inflation (p.a.)" hint={inflation + "%"}><SliderRow testid="goal-inflation" value={inflation} min={0} max={12} step={0.5} suffix="%" onChange={setInflation} /></Field>
              <Field label="Current savings for this goal"><MoneyInput testid="goal-savings" value={currentSavings} onChange={setSavings} helper={compactINR(currentSavings)} /></Field>
            </div>
          </SectionCard>
        }
        right={
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <StatCard testid="goal-sip" label="Required Monthly SIP" value={compactINR(r.requiredSip)} accent="#D96B43" big />
              <StatCard testid="goal-lumpsum" label="Or one-time Lumpsum" value={compactINR(r.requiredLumpsum)} accent="#2D6A4F" />
            </div>
            <SectionCard title="The maths">
              <InfoRow label="Goal today" value={compactINR(goalToday)} />
              <InfoRow label={`Goal in ${years} yrs (inflation-adjusted)`} value={compactINR(r.targetFuture)} strong />
              <InfoRow label="Your current savings will grow to" value={compactINR(r.fvSavings)} />
              <InfoRow label="Gap to fund" value={compactINR(r.need)} strong />
            </SectionCard>
            <ToolAdvisor tool="goal" title="Goal Planner"
              context={[
                { label: "Goal today", value: compactINR(goalToday) },
                { label: "Years", value: `${years}` },
                { label: "Inflation-adjusted goal", value: compactINR(r.targetFuture) },
                { label: "Expected return", value: `${rate}%` },
                { label: "Current savings", value: compactINR(currentSavings) },
                { label: "Required monthly SIP", value: compactINR(r.requiredSip) },
              ]} />
          </>
        }
      />
    </ToolLayout>
  );
}
